import axios from "axios";
import { showErrorToast } from "../utils/Toasts";

// Null-safe: Rails omits csrf_meta_tags when forgery protection is off (the
// test env / system-spec harness) — a missing tag must not reject EVERY axios
// request at the interceptor (PAI-370). Absent token → header simply not set.
const getCsrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

const axiosClient = axios.create({
  baseURL: "/",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

axiosClient.interceptors.request.use(
  (config) => {
    const csrfToken = getCsrfToken();
    if (csrfToken) config.headers["X-CSRF-Token"] = csrfToken;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
// PAI-547 — machine codes are for callers, not people. Rails endpoints return
// snake_case codes in data.error ("already_generating", "cooldown", …); when a
// surface hasn't opted out of the global toast, never show a bare code raw.
const GENERIC_ERROR_COPY = "Something went wrong. Please try again.";
const MACHINE_CODE = /^[a-z0-9_]+$/;
const humanize = (message) => (MACHINE_CODE.test(message) ? GENERIC_ERROR_COPY : message);

axiosClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // PAI-547 — per-request opt-out. The Opportunities island sets this via its
    // makeOpportunitiesRequestConfig chokepoint: it surfaces its own error copy,
    // so the global toast would double-toast (and fire once per failing poll tick).
    if (error.config?.suppressErrorToast) return Promise.reject(error);

    if (error.response) {
      const { error: singleError, errors: multipleErrors } = error.response.data || {};
      let messages = [];

      if (typeof singleError === "string") {
        messages = [singleError];
      } else if (Array.isArray(multipleErrors)) {
        messages = multipleErrors;
      } else if (typeof multipleErrors === "string") {
        messages = [multipleErrors];
      } else {
        messages = ["Something went wrong!"];
      }

      // Strip stray quoting, humanize codes, and dedupe — a response is never
      // worth more than one identical toast (PAI-547).
      const unique = [...new Set(messages.map((msg) => humanize(msg.replace(/^["']|["']$/g, ""))))];
      unique.forEach((msg) => showErrorToast(msg));
    } else if (error.request) {
      showErrorToast("No response from the server. Please try again.");
    } else {
      showErrorToast("Request error. Please check your connection.");
    }
    return Promise.reject(error);
  }
);

export default axiosClient;
